import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';

import { useAuth } from '~/auth/use-auth';
import { getSocketInstance } from '~/libs/socket';

interface SocketContextValue {
  socket: Socket;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!isAuthenticated) {
      if (socket) {
        console.debug('Disconnecting socket because user is not authenticated');
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    const newSocket = getSocketInstance();
    newSocket.connect();
    setSocket(newSocket);

    return () => {
      console.debug('Disconnecting socket on unmount');
      newSocket.disconnect();
    };
  }, [isAuthenticated]);

  if (!socket) {
    return children;
  }

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
